import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Textarea } from '../../../shared/components/Textarea';
import { api } from '../../../shared/lib/axios';
import type { DecideApprovalPayload, DecisaoAprovacaoProposta, PropostaAprovacao } from '../api';
import { PROPOSAL_KEYS } from '../api';
import { useDecidirAprovacao } from '../hooks';
import { DECISAO_APROVACAO_LABEL } from '../utils/format';

interface PendingApprovalRow extends PropostaAprovacao {
  proposalId: string;
  proposalCode: string;
  proposalVersionNumber: string;
}

/**
 * Painel de aprovações (US-COM-006, task 15.6). O usuário aprovador vê
 * apenas as pendências que ele consegue decidir — filtragem por
 * `feature_required` é resolvida no backend (`/proposal-approvals/pending`).
 *
 * Ações inline: aprovar, devolver (aguardando complementação), rejeitar
 * (devolve para revisão). Cada decisão dispara mutation com invalidação
 * ampla (`PROPOSAL_KEYS.all`) — o último step promove a versão para
 * aprovada_internamente, então listas de status ficam stale.
 */
export default function AprovacoesPropostasPage(): React.ReactElement {
  const navigate = useNavigate();

  const pendentes = useQuery({
    queryKey: [...PROPOSAL_KEYS.all, 'pending-approvals'],
    queryFn: async () => {
      const { data } = await api.get<PendingApprovalRow[] | { data: PendingApprovalRow[] }>(
        `/proposal-approvals/pending`,
      );
      return Array.isArray(data) ? data : data.data;
    },
  });

  const decidir = useDecidirAprovacao();

  const [openComment, setOpenComment] = useState<{
    approvalId: string;
    decision: DecideApprovalPayload['decision'];
  } | null>(null);
  const [comment, setComment] = useState('');

  async function decide(row: PendingApprovalRow, decision: DecideApprovalPayload['decision']) {
    if (decision === 'rejeitada' || decision === 'devolvida') {
      setOpenComment({ approvalId: row.id, decision });
      return;
    }
    await decidir.mutateAsync({ approvalId: row.id, payload: { decision } });
  }

  async function confirmarComComentario() {
    if (!openComment) return;
    await decidir.mutateAsync({
      approvalId: openComment.approvalId,
      payload: { decision: openComment.decision, comment: comment.trim() || undefined },
    });
    setOpenComment(null);
    setComment('');
  }

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate('/propostas')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <h1 className="mb-2 text-xl font-semibold text-gray-900">Aprovações de Propostas</h1>
      <p className="mb-6 text-sm text-gray-600">
        Etapas do workflow parametrizável aguardando sua decisão.
      </p>

      {pendentes.isLoading && <Skeleton className="h-20 w-full" />}
      {pendentes.isError && (
        <Alert variant="error">
          Erro ao carregar pendências: {(pendentes.error as Error)?.message ?? 'erro'}
        </Alert>
      )}
      {!pendentes.isLoading && (pendentes.data ?? []).length === 0 && (
        <EmptyState title="Sem pendências" description="Nenhuma proposta aguardando sua decisão." />
      )}
      {!pendentes.isLoading && (pendentes.data ?? []).length > 0 && (
        <ul className="space-y-2">
          {(pendentes.data ?? []).map((row) => (
            <li
              key={row.id}
              className="rounded border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <button
                    type="button"
                    onClick={() => navigate(`/propostas/${row.proposalId}`)}
                    className="text-sm font-medium text-blue-700 hover:underline"
                  >
                    {row.proposalCode} · v{row.proposalVersionNumber}
                  </button>
                  <div className="mt-0.5 text-xs text-gray-500">
                    Etapa {row.stepIndex + 1} · Role {row.roleSlug} · Feature {row.featureRequired}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => decide(row, 'aprovada')} disabled={decidir.isPending}>
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                    Aprovar
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => decide(row, 'devolvida')}
                    disabled={decidir.isPending}
                  >
                    <RotateCcw className="mr-1 h-4 w-4" />
                    Devolver
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => decide(row, 'rejeitada')}
                    disabled={decidir.isPending}
                  >
                    <XCircle className="mr-1 h-4 w-4" />
                    Rejeitar
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {openComment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {openComment.decision === 'rejeitada' ? 'Rejeitar' : 'Devolver'} etapa
            </h3>
            <p className="mb-3 mt-1 text-sm text-gray-600">
              Informe um comentário para o responsável comercial saber o que ajustar.
            </p>
            <Textarea
              label="Comentário (opcional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setOpenComment(null);
                  setComment('');
                }}
              >
                Cancelar
              </Button>
              <Button onClick={confirmarComComentario} disabled={decidir.isPending}>
                Confirmar {DECISAO_APROVACAO_LABEL[openComment.decision as DecisaoAprovacaoProposta]}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { AprovacoesPropostasPage };
